import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { Document, Page, Text, View, StyleSheet, Image, Link } from "@react-pdf/renderer";
import { DomUtils, parseDocument } from "htmlparser2";
import axios from "axios";


export const renderNoteAsPDF = (html) => {
  const doc = parseDocument(html);
  const nodes = DomUtils.getChildren(doc);

  const renderNodes = (elements, depth = 0) => {
    return elements.map((node, idx) => {
      if (node.type === 'text') {
        return node.data;
      }

      if (node.name === 'br') {
        return <Text key={`br-${depth}-${idx}`}>{'\n'}</Text>;
      }

      if (node.name === 'p') {
        return (
          <Text key={`p-${depth}-${idx}`} style={styles.paragraph}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }
      if (node.name === 'a') {
        const href = node.attribs?.href || "#";
        const linkText = DomUtils.getText(node); // 🔥 Plain text from inside <a>
        return (
          <Link
            key={`a-${depth}-${idx}`}
            src={href.startsWith("http") ? href : `https://${href}`} // ensure it's clickable
            style={{ color: 'blue', textDecoration: 'none' }} // removes underline
          >
            {linkText}
          </Link>
        );
      }


      if (node.name === 'u') {
        return (
          <Text key={`u-${depth}-${idx}`} style={{ textDecoration: 'underline' }}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }



      if (node.name === 'strong' || node.name === 'b') {
        return (
          <Text key={`b-${depth}-${idx}`} style={{ fontWeight: 'bold' }}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }

      if (node.name === 'em' || node.name === 'i') {
        return (
          <Text key={`i-${depth}-${idx}`} style={{ fontStyle: 'italic' }}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }

      if (node.name === 'ul') {
        return DomUtils.getChildren(node)
          .filter(n => n.name === 'li')
          .map((li, i) => (
            <Text key={`ul-${depth}-${idx}-${i}`} style={styles.listItem}>
              • {renderNodes(DomUtils.getChildren(li), depth + 1)}
            </Text>
          ));
      }

      if (node.name === 'ol') {
        return DomUtils.getChildren(node)
          .filter(n => n.name === 'li')
          .map((li, i) => (
            <Text key={`ol-${depth}-${idx}-${i}`} style={styles.listItem}>
              {`${i + 1}. ${renderNodes(DomUtils.getChildren(li), depth + 1)}`}
            </Text>
          ));
      }

      // fallback for unknown tags
      return renderNodes(DomUtils.getChildren(node), depth + 1);
    });
  };

  return renderNodes(nodes);
};


const calculateGST = (taxable, taxRate) => {
  const rate = Number(taxRate) || 0;

  if (rate === 0) {
    return {
      cgstRate: 0,
      sgstRate: 0,
      cgst: 0,
      sgst: 0,
    };
  }

  const halfRate = rate / 2;

  return {
    cgstRate: halfRate,
    sgstRate: halfRate,
    cgst: (taxable * halfRate) / 100,
    sgst: (taxable * halfRate) / 100,
  };
};

const numberToIndianWords = (num) => {
  if (!num || isNaN(num)) return "Zero Rupees";

  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six",
    "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
    "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];

  const b = [
    "", "", "Twenty", "Thirty", "Forty",
    "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
  ];

  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + inWords(n % 100) : "");
    return "";
  };

  let number = Math.floor(num);
  let words = "";

  if (number >= 10000000) {
    words += inWords(Math.floor(number / 10000000)) + " Crore ";
    number %= 10000000;
  }
  if (number >= 100000) {
    words += inWords(Math.floor(number / 100000)) + " Lakh ";
    number %= 100000;
  }
  if (number >= 1000) {
    words += inWords(Math.floor(number / 1000)) + " Thousand ";
    number %= 1000;
  }
  if (number > 0) {
    words += inWords(number);
  }

  return words.trim() + " Rupees";
};


const styles = StyleSheet.create({
  receiptCenter: { textAlign: "center", fontSize: "18px", marginBottom: "5px", fontWeight: 600 },
  page: { padding: 20, backgroundColor: "#fff", fontSize: 12, fontFamily: "Helvetica" },
  headerBox: {
    backgroundColor: "#d1dffa",
    padding: 10,
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  headerTop: { fontSize: 18, color: "#444", marginTop: "5px", fontWeight: "bold" },
  logoSection: { flex: 1 },
  headerP: { fontSize: 12, color: "#444", marginTop: "5px" },
  logoWrap: { flexDirection: "row", alignItems: "center" },
  logo1: { width: 100, height: 40, marginRight: 8 },
  addressText: { fontSize: 11, marginTop: 6 },
  headerRight: {
    textAlign: "right",
    fontSize: 12,
    color: "#333",
    lineHeight: 1.6,
    flex: 1
  },
  section: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  bold: { fontSize: 12, fontWeight: "bold" },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#d1dffa",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderColor: "#ccc",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#eee",
    paddingVertical: 5,
  },
  cell: { flex: 1, textAlign: "center" },
  cellWide: { flex: 2, textAlign: "center" },
  summaryBox: {
    borderWidth: 1,
    borderColor: "#ccc",
    marginTop: 10,
    borderRadius: 6,
    padding: 10,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 2,
    fontSize: 12,
  },
  grandTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#003366",
    padding: 10,
    marginTop: 10,
    borderRadius: 4,
  },
  grandTotalText: {
    color: "white",
    fontSize: 10,
    fontWeight: "bold",
  },

  noteSection: { marginTop: 10 },
  noteLabel: { fontSize: 12, fontWeight: "bold", marginBottom: 4 },
  noteText: { fontSize: 12 },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },

  box: {
    borderWidth: 1,
    borderColor: "#000",
  },

  cell: {
    padding: 6,
  },

  bold: {
    fontWeight: "bold",
  },
  gstTable: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: "#000",
  },

  gstRow: {
    flexDirection: "row",
  },

  gstCell: {
    padding: 6,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    textAlign: "center",
  },
  semibold: {
    fontWeight: 500,
    marginBottom: 5,
  },
  gstHeader: {
    backgroundColor: "#eaeaea",
    fontWeight: "bold",
    textAlign: "center",
  },

  gstRight: {
    textAlign: "right",
  },

  gstBold: {
    fontWeight: "bold",
  },
  itemTable: {
    borderWidth: 1,
    borderColor: "#000",
  },

  itemRow: {
    flexDirection: "row",
  },

  itemHeaderCell: {
    padding: 6,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    backgroundColor: "#f2f2f2",
    fontWeight: "bold",
    textAlign: "center",
  },

  itemCell: {
    padding: 6,
    borderRightWidth: 1,
    // borderBottomWidth: 1,
    borderColor: "#000",
  },

  itemlastcelltable: {
    padding: 6,
    borderRightWidth: 1,
    // borderBottomWidth: 1,
    borderColor: "#000",
  },

  itemLastCell: {
    padding: 6,
    // borderBottomWidth: 1,
    borderColor: "#000",
  },
  itemLastCelltable: {
    padding: 6,
    // borderBottomWidth: 1,
    borderColor: "#000",
  },

  marginTopitemCell: {
    marginTop: 4
  },
});

const ReceiptDocument = ({ receipt, profile, invoice, logoBase64, signUrl, bankDetails, authorityData, base64Sign, customer }) => {
  const logoImg = `${API_BASE_URL}/${profile.orignal_logoname}` || "";
  const address = profile?.office_address || "";
  const phone = profile?.mobile || "";
  const email = profile?.email || "";
  const taxNo = profile?.taxNo
  const [imagedata, setImageData] = useState()
  const [imagebase64, setImageBase64] = useState()

  let items = [];
  try {
    if (Array.isArray(invoice?.items)) {
      items = invoice.items;
    } else if (typeof invoice?.items === "string") {
      let parsed = JSON.parse(invoice.items);
      if (typeof parsed === "string") parsed = JSON.parse(parsed); // handle double stringification
      if (Array.isArray(parsed)) items = parsed;
    }
  } catch (e) {
    console.warn("❌ Failed to parse invoice items", e);
    items = [];
  }

  items.forEach((it, i) => {
  });


  const parseAddress = (addressString) => {
    try {
      if (!addressString) return null;
      const parsed = JSON.parse(addressString);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return "-";

    const parts = [
      addr.address,
      addr.city,
      addr.state,
      addr.pincode,
      addr.country,
    ].filter(Boolean);

    return parts.length ? parts.join(", ") : "-";
  };


  const getImageAsBase64 = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob); // returns base64 string
    });
  };

  const totals = items.reduce(
    (acc, i) => {
      const qty = Number(i.quantity) || 0;
      const rate = Number(i.rate) || 0;
      const taxable = qty * rate;

      const gst = calculateGST(taxable, i.tax);
      acc.taxable += taxable;
      acc.cgst += gst.cgst;
      acc.sgst += gst.sgst;
      return acc;
    },
    { taxable: 0, cgst: 0, sgst: 0 }
  );

  const grandTotal =
    totals.taxable + totals.cgst + totals.sgst;

  const gstRate =
    items.length > 0 ? Number(items[0].tax) || 0 : 0;

  const cgstRate = gstRate / 2;
  const sgstRate = gstRate / 2;

  const grandTotalWords = numberToIndianWords(grandTotal);

  const billingAddrObj = parseAddress(customer?.billingAddress);
  const shippingAddrObj = parseAddress(customer?.shippingAddress);

  const billingAddress = formatAddress(billingAddrObj);
  const shippingAddress = formatAddress(shippingAddrObj);


  useEffect(() => {
    getImageAsBase64("https://ecommerce.100billionoms.com/image-stamp").then(setImageBase64);
  }, []);

  const GST_COLS = {
    hsn: 80,
    taxable: 120,
    rate: 50,
    amount: 70,
    total: 120,
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={[styles.row, { marginBottom: 6 }]}>
          {/* TAX INVOICE (NO BOX) */}
          <Text style={[styles.bold, { fontSize: 12 }]}>TAX INVOICE</Text>

          {/* ORIGINAL FOR RECIPIENT (BOXED) */}
          <View style={[styles.box, { marginLeft: 10, paddingHorizontal: 6, paddingVertical: 2 }]}>
            <Text>ORIGINAL FOR RECIPIENT</Text>
          </View>
        </View>
        <View style={[styles.box, styles.row]}>

          {/* -------- LEFT : COMPANY -------- */}
          <View style={[styles.cell, { width: "60%", borderRightWidth: 1 }]}>

            <View style={styles.row}>
              {logoBase64 && (
                <Image
                  src={logoBase64}
                  style={{ width: 48, height: 48, marginRight: 8 }}
                />
              )}

              <View style={{ width: "100%" }}>
                <Text style={[styles.bold, { fontSize: 12 }]}>
                  {authorityData.company_name}
                </Text>

                {/* Address constrained inside box */}
                <Text style={{ fontSize: 10 }}>{profile.office_address}</Text>
                {/* <Text>
                                            {profile.city}, {profile.state}, {profile.pincode}
                                        </Text> */}
                {
                  profile.taxNo &&
                  <Text style={[{ fontSize: 10 }]}>GSTIN: {profile.taxNo}</Text>
                }

                {
                  profile.mobile &&
                  <Text style={[{ fontSize: 10 }]}>Mobile: {profile.mobile}</Text>
                }

                {profile.pan &&
                  <Text style={[{ fontSize: 10 }]}>PAN Number: {profile.pan}</Text>
                }

              </View>
            </View>

          </View>

          {/* -------- RIGHT : INVOICE INFO -------- */}
          <View style={{ width: "40%" }}>

            {/* Row 1 */}
            <View style={[styles.row, { borderBottomWidth: 0 }]}>
              <View style={[styles.cell, { width: "50%", borderRightWidth: 0 }]}>
                <Text style={[styles.bold, { fontSize: 12 }]}>Invoice No.</Text>
                <Text style={{ fontSize: 10 }}>{invoice.invoice_number}</Text>
              </View>

              <View style={[styles.cell]}>
                <Text style={[styles.bold, { fontSize: 12 }]}>Invoice Date</Text>
                <Text style={{ fontSize: 10 }}>{invoice.invoice_date}</Text>
              </View>
            </View>

            {/* Row 2 */}
            {/* <View style={styles.cell}>
                                    <Text style={styles.bold}>P.O. No.</Text>
                                    <Text>{invoice.order_number}</Text>
                                </View> */}

          </View>
        </View>

        {/* Billing Info */}
        <View style={[styles.box, styles.row, { borderTopWidth: 0 }]}>

          {/* LEFT = 60% (same as company block) */}
          <View
            style={[
              styles.cell,
              {
                width: "60%",
                borderRightWidth: 1,   // ✅ aligns with company divider
                borderColor: "#000",
              },
            ]}
          >
            <Text style={[styles.semibold, { fontSize: 12 }]}>BILL TO</Text>
            <Text style={[styles.bold, { fontSize: 10 }]}>{invoice.customer_name}</Text>
            {billingAddress && billingAddress !== "-" && (
              <Text style={{ fontSize: 10 }}>Address: {billingAddress}</Text>
            )}
            {(profile.taxNo || profile.city || profile.state || profile.pincode) && (
              <Text style={{ fontSize: 10 }}>
                {profile.taxNo && `GSTIN: ${profile.taxNo} `}
                {(profile.city || profile.state || profile.pincode) && (
                  `Place of Supply: ${[profile.city, profile.state, profile.pincode]
                    .filter(Boolean)
                    .join(", ")
                  }`
                )}
              </Text>
            )}
          </View>

          {/* RIGHT = 40% */}
          <View style={{ width: "40%", padding: 6 }}>
            <Text style={[styles.semibold, { fontSize: 12 }]}>SHIP TO</Text>
            <Text style={[styles.bold, { fontSize: 10 }]}>{invoice.customer_name}</Text>
            <Text style={{ fontSize: 10 }}>Address: {shippingAddress}</Text>
          </View>

        </View>

        {/* Items Table */}


        <View style={[styles.itemTable, { borderTopWidth: 0, borderBottomWidth: 0 }]}>
          {/* ---------- HEADER ---------- */}
          <View style={[styles.itemRow]}>
            <Text style={[styles.itemHeaderCell, { width: 30, fontSize: 12 }]}>NO.</Text>
            <Text style={[styles.itemHeaderCell, { width: 260, fontSize: 12 }]}>ITEMS / SERVICES</Text>
            <Text style={[styles.itemHeaderCell, { width: 80, fontSize: 12 }]}>HSN/SAC</Text>
            <Text style={[styles.itemHeaderCell, { width: 60, fontSize: 12 }]}>QTY.</Text>
            <Text style={[styles.itemHeaderCell, { width: 60, fontSize: 12 }]}>RATE</Text>
            <Text style={[styles.itemLastCell, styles.itemHeaderCell, { width: 80, borderRightWidth: 0, fontSize: 12 }]}>AMOUNT</Text>
          </View>

          {/* <View style={[{ position: "relative" }]}>
            <Image
              src="https://ecommerce.100billionoms.com/image-stamp"
              style={{
                position: "absolute",
                top: 40,
                left: 140,
                width: 100,
                height: 70,
                opacity: 1,
              }}
            />
          </View> */}
          {items.map((i, idx) => {
            const qty = Number(i.quantity) || 0;
            const rate = Number(i.rate) || 0;
            const taxable = qty * rate;

            const gst = calculateGST(taxable, i.tax);

            return (
              <View key={idx} style={styles.itemRow}>
                <Text style={[styles.itemCell, { width: 30, fontSize: 10 }]}>{idx + 1}</Text>

                <Text style={[styles.itemCell, { width: 260, fontSize: 10 }]}>
                  {(i.item?.title || "Item").replace(/\s*-?\s*Sku-.*/i, "").trim()}
                </Text>

                <Text style={[styles.itemCell, { width: 80, fontSize: 10 }]}>{i.hsn || "-"}</Text>

                <Text style={[styles.itemCell, { width: 60, textAlign: "center", fontSize: 10 }]}>
                  {qty}
                </Text>

                <Text style={[styles.itemCell, { width: 60, textAlign: "right", fontSize: 10 }]}>
                  {rate.toFixed(2)}
                </Text>

                <Text style={[styles.itemLastCell, { width: 80, textAlign: "right", fontSize: 10 }]}>
                  {taxable.toFixed(2)}
                </Text>
              </View>
            );
          })}


          <View style={[styles.itemRow, { height: 20, position: "relative" }]}>
            <Image
              src="https://ecommerce.100billionoms.com/image-stamp"
              style={{
                position: "absolute",
                top: -20,
                left: 140,
                width: 100,
                height: 70,
                opacity: 1,
              }}
            />
            <Text style={[styles.itemCell, { width: 30 }]} />
            <Text style={[styles.itemCell, { width: 260 }]} />
            <Text style={[styles.itemCell, { width: 80 }]} />
            <Text style={[styles.itemCell, { width: 60 }]} />
            <Text style={[styles.itemCell, { width: 60 }]} />
            <Text style={[styles.itemLastCell, { width: 80 }]} />
          </View>

          {/* ---------- BLANK FILLER (keeps CGST at bottom) ---------- */}
          {/* <View style={[styles.itemRow, { height: 160, position: "relative" }]}>
            
           
          </View> */}

          {/* ---------- CGST ---------- */}
          <View style={styles.itemRow}>
            <Text style={[styles.itemCell, { width: 30 }]} />
            <Text style={[styles.itemCell, styles.gstRight, { width: 260, fontSize: 10 }]}>CGST @{cgstRate}%</Text>
            <Text style={[styles.itemCell, { width: 80 }]}>-</Text>
            <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
            <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
            <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0, fontSize: 10 }]}>Rs. {totals.cgst.toFixed(2)}</Text>
          </View>

          {/* ---------- SGST ---------- */}
          <View style={styles.itemRow}>
            <Text style={[styles.itemCell, { width: 30 }]} />
            <Text style={[styles.itemCell, styles.gstRight, { width: 260, fontSize: 10 }]}> SGST @{sgstRate}%</Text>
            <Text style={[styles.itemCell, { width: 80 }]}>-</Text>
            <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
            <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
            <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0, fontSize: 10 }]}>Rs. {totals.sgst.toFixed(2)}</Text>
          </View>

          {/* ---------- TOTAL ---------- */}
          <View style={[styles.itemRow, { borderTopWidth: 1 }]}>
            <Text style={[styles.itemHeaderCell, { width: 30 }]} />
            <Text style={[styles.itemHeaderCell, styles.bold, styles.gstRight, { width: 260, fontSize: 10 }]}>TOTAL</Text>
            <Text style={[styles.itemHeaderCell, { width: 80 }]} />
            <Text style={[styles.itemHeaderCell, { width: 60 }]} />
            <Text style={[styles.itemHeaderCell, { width: 60 }]} />
            <Text style={[styles.itemHeaderCell, styles.bold, styles.gstRight, { width: 80, borderRightWidth: 0, fontSize: 10 }]}>Rs. {grandTotal.toFixed(2)}</Text>
          </View>

        </View>
        {/* Summary Box */}
        <View style={styles.noteSection}>
          <Text style={styles.noteLabel}>Receipt Details:</Text>

        </View>

        <View style={styles.summaryBox}>
          <View style={styles.summaryRow}>
            <Text>Amount Paid</Text>
            <Text>Rs.{parseFloat(receipt.amount).toFixed(2)}</Text>
          </View>

        </View>

        {/* Grand Total */}
        {/* <Text style={styles.grandTotal}>
          Receipt Generated for Rs.{parseFloat(receipt.amount).toFixed(2)}
          <Text style={styles.noteLabel}>Pending Amount: Rs.{parseFloat(invoice?.pending || 0).toFixed(2)}</Text>
        </Text> */}

        <View style={styles.grandTotal}>
          <Text style={styles.grandTotalText}>
            Receipt Generated: Rs. {parseFloat(receipt.amount).toFixed(2)}
          </Text>

          <Text style={styles.grandTotalText}>
            Pending: Rs. {parseFloat(invoice?.pending || 0).toFixed(2)}
          </Text>
        </View>
        <View style={styles.noteSection}>


        </View>
        <View style={[styles.gstTable, { borderRightWidth: 0 }]}>

          {/* HEADER ROW 1 */}
          <View style={[styles.gstRow]}>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.hsn }]}>
              HSN/SAC
            </Text>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.taxable }]}>
              Taxable Value
            </Text>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate + GST_COLS.amount }]}>
              CGST
            </Text>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate + GST_COLS.amount }]}>
              SGST
            </Text>
            <Text style={[styles.gstCell, styles.gstHeader, { flex: 1 }]}>
              Total Tax Amount
            </Text>
          </View>


          {/* HEADER ROW 2 */}
          <View style={[styles.gstRow, { borderBottomWidth: 0 }]}>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.hsn }]} />
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.taxable }]} />

            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate }]}>
              Rate
            </Text>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.amount }]}>
              Amount
            </Text>

            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate }]}>
              Rate
            </Text>
            <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.amount }]}>
              Amount
            </Text>

            <Text style={[styles.gstCell, styles.gstHeader, { flex: 1 }]} />

          </View>


          {/* DATA ROWS */}
          {items.map((i, idx) => {
            const taxable = i.rate * i.quantity;
            const gst = calculateGST(taxable, i.tax);

            return (
              <View key={idx} style={styles.gstRow}>
                <Text style={[styles.gstCell, { width: GST_COLS.hsn }]}>{i.hsn}</Text>

                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.taxable }]}>
                  {taxable.toLocaleString()}
                </Text>

                <Text style={[styles.gstCell, { width: GST_COLS.rate }]}>{i.tax / 2}%</Text>
                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.amount }]}>
                  {gst.cgst.toFixed(1)}
                </Text>

                <Text style={[styles.gstCell, { width: GST_COLS.rate }]}>{i.tax / 2}%</Text>
                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.amount }]}>
                  {gst.sgst.toFixed(1)}
                </Text>

                <Text style={[styles.gstCell, styles.gstRight, { flex: 1 }]}>
                  Rs. {(gst.cgst + gst.sgst).toFixed(1)}
                </Text>


              </View>
            );
          })}

          {/* TOTAL ROW */}
          <View style={styles.gstRow}>
            <Text style={[styles.gstCell, styles.gstBold, { width: GST_COLS.hsn, borderBottomWidth: 0 }]}>
              Total
            </Text>

            <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.taxable, borderBottomWidth: 0 }]}>
              {totals.taxable.toLocaleString()}
            </Text>

            <Text style={[styles.gstCell, { width: GST_COLS.rate, borderBottomWidth: 0 }]} />
            <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.amount, borderBottomWidth: 0 }]}>
              {totals.cgst.toFixed(1)}
            </Text>

            <Text style={[styles.gstCell, { width: GST_COLS.rate, borderBottomWidth: 0 }]} />
            <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.amount, borderBottomWidth: 0 }]}>
              {totals.sgst.toFixed(1)}
            </Text>

            <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { flex: 1, borderBottomWidth: 0 }]}>
              Rs. {(totals.cgst + totals.sgst).toFixed(1)}
            </Text>
          </View>



        </View>


        {/* Total in words, bank + signatory row, then notes (same order as invoice PDF) */}
        <View wrap={false}>
          <View style={[styles.box, { marginTop: 8, padding: 6 }]}>
            <Text style={styles.bold}>Total Amount (in words)</Text>
            <Text style={{ marginTop: 4 }}>
              {grandTotalWords}
            </Text>
          </View>

          {/* ================= FOOTER ================= */}
          <View style={[styles.box, styles.row, { borderTopWidth: 0 }]}>
            <View
              style={{
                width: "50%",
                padding: 6,
                borderRightWidth: 1,
                alignSelf: "stretch",
              }}
            >
              <Text style={styles.bold}>Bank Details</Text>
              <Text style={styles.marginTopitemCell}>Name: {bankDetails?.bank_name}</Text>
              <Text style={styles.marginTopitemCell}>IFSC Code: {bankDetails?.ifsc_code}</Text>
              <Text style={styles.marginTopitemCell}>Account No: {bankDetails?.account_no}</Text>
              <Text style={styles.marginTopitemCell}>Bank: {bankDetails?.bank}</Text>
            </View>

            <View
              style={{
                width: "50%",
                padding: 6,
                borderColor: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {base64Sign && <Image src={base64Sign} style={{ width: 80, height: 40 }} />}
              <Text style={[styles.center, { marginTop: 4, fontSize: 8 }]}>
                Authorised Signatory For{"\n"}

              </Text>
              <Text style={[styles.center, { marginTop: 4, fontSize: 8 }]}>
                {authorityData.company_name}

              </Text>

            </View>
          </View>

          {/* ================= NOTES AT BOTTOM (matches invoice) ================= */}
          <View
            style={{
              marginTop: 10,
              paddingTop: 6,
            }}
          >
            <Text style={styles.bold}>Notes:</Text>
            <View style={{ marginTop: 4 }}>
              {renderNoteAsPDF(invoice.customer_note || '<p>No notes</p>')}
            </View>
          </View>
        </View>

      </Page>
    </Document>
  );
};

export default ReceiptDocument;
